import { Component, computed, inject, output, signal, viewChild } from '@angular/core';
import { SurveyChatService } from '../../core/survey-chat.service';
import { ChatIdentityService } from '../../core/chat-identity.service';
import { ChatStaffComponent } from './chat-staff.component';
import { ChatTabsComponent } from './chat-tabs.component';
import { ChatThreadComponent } from './chat-thread.component';
import { ChatComposerComponent } from './chat-composer.component';
import { ChatBreakComponent } from './chat-break.component';

/**
 * The panel shell: header, break control, tabs, and either the staff list or a
 * conversation. It is the one place that holds a service and hands its signals
 * down, which is what keeps the five components below it pure input/output.
 *
 * Two things it owns that no child can:
 *
 *  - **Handing typed text back.** The composer and the staff box both clear
 *    optimistically the moment Send is hit, and `SurveyChatService` swallows the
 *    failure and only sets `error()`. This is the only place that holds both the
 *    text it passed in and whether the API refused.
 *  - **Which error belongs to the break.** `error()` is one signal, written by
 *    `send`, `startWith`, `closeChat`, `reopen` and both break calls. Bound
 *    straight into `[refusal]`, "that message was not sent" would print inside
 *    the break control, styled as a refused break and announced as one. The
 *    break's own refusal is kept in `breakRefusal`, set only from the result of
 *    a break call; everything else goes to the panel's own line.
 *
 * There is no business-hours banner and no queue: spec section 2, decision 3
 * puts the hours gate on `origin_app = 'portal'` only, and spec section 9 gives
 * survey the online staff list plus its own conversations.
 *
 * Every class is prefixed `afs-chat-` and lives in this component's own inline
 * styles, so NobleUI's demo `.chat-*` set cannot reach it.
 */
@Component({
  selector: 'app-chat-panel',
  standalone: true,
  imports: [
    ChatStaffComponent,
    ChatTabsComponent,
    ChatThreadComponent,
    ChatComposerComponent,
    ChatBreakComponent,
  ],
  styles: [`
    .afs-chat-panel {
      position: fixed; right: 1.25rem; bottom: 5.25rem; z-index: 1040;
      display: flex; flex-direction: column;
      width: min(420px, calc(100vw - 2.5rem));
      /* 90% of the viewport, with a floor that keeps the panel on screen on a
         short window: it sits at bottom: 5.25rem, so 5.5rem is the least that
         still clears the bubble. The floor only bites below about 880px tall.
         height, not max-height: a cap only limits the panel, which is otherwise
         sized by its content. A short conversation therefore left it a few
         hundred pixels tall, and the flex children below (flex-basis 0) then
         collapsed to their min-height and grew scrollbars. A definite height is
         what makes 90% of the viewport actually happen. */
      height: min(90vh, calc(100vh - 5.5rem));
      border: 1px solid rgba(0,0,0,.12); border-radius: 4px;
      background: var(--bs-body-bg, #fff); box-shadow: 0 12px 32px rgba(0,0,0,.22);
      overflow: hidden;
    }
    /* These styles are a template literal, so no backticks below.
       The panel's flex children are the components' HOST elements —
       app-chat-thread, app-chat-composer, app-chat-staff — not the classed divs
       inside them, and nothing styled a host. So flex:1 on .afs-chat-thread was
       read by a parent that is not a flex container and did nothing: the thread
       grew to the full height of every message it held, its own overflow-y:auto
       never engaged, and the actions row and the composer were pushed past this
       panel's overflow:hidden and clipped off the bottom.
       Same defect, same fix, as the CRM and portal panels. */
    .afs-chat-panel > * { flex-shrink: 0; }
    .afs-chat-panel > app-chat-thread,
    .afs-chat-panel > app-chat-staff {
      display: flex; flex-direction: column; flex: 1 1 0; min-height: 5rem;
    }
    .afs-chat-panel__header {
      display: flex; flex-wrap: wrap; align-items: center; gap: .4rem;
      padding: .5rem .6rem; border-bottom: 1px solid rgba(0,0,0,.12);
    }
    .afs-chat-panel__title { flex: 1; margin: 0; font-size: .95rem; font-weight: 600; }
    .afs-chat-panel__sound-label {
      display: inline-flex; align-items: center; gap: .25rem; font-size: .75rem;
    }
    .afs-chat-panel__close {
      border: 0; background: transparent; font-size: 1.25rem; line-height: 1; cursor: pointer;
    }
    .afs-chat-panel__error {
      margin: 0; padding: .45rem .6rem; font-size: .8rem; background: rgba(255,51,102,.16);
    }
    .afs-chat-panel__actions {
      display: flex; gap: .35rem; padding: .4rem .6rem;
      border-top: 1px solid rgba(0,0,0,.1);
    }
    @media (pointer: coarse) {
      .afs-chat-panel { width: calc(100vw - 1.5rem); right: .75rem; }
      .afs-chat-panel__close { min-width: 44px; min-height: 44px; }
    }
  `],
  template: `
    <section class="afs-chat-panel" role="dialog" aria-label="Chat"
             tabindex="-1" (keydown)="onKeydown($event)">
      <header class="afs-chat-panel__header">
        <h2 class="afs-chat-panel__title">Chat</h2>

        <app-chat-break
          [duty]="chat.duty()"
          [refusal]="breakRefusal()"
          (start)="onStartBreak($event)"
          (end)="onEndBreak()"
        />

        <label class="afs-chat-panel__sound-label">
          <input class="afs-chat-panel__sound" type="checkbox"
                 [checked]="chat.soundOn()"
                 (change)="chat.setSound($any($event.target).checked)" />
          Sound
        </label>

        @if (chat.desktopPermission() === 'unasked') {
          <!-- Asked on a click, never on load: a prompt on load is what makes
               people block a site permanently. -->
          <button class="afs-chat-panel__desktop btn btn-sm btn-outline-secondary"
                  type="button" (click)="chat.askDesktopPermission()">Enable alerts</button>
        }

        <button class="afs-chat-panel__close" type="button" aria-label="Close chat"
                (click)="dismiss.emit()">×</button>
      </header>

      <!-- No business-hours banner. Spec section 2, decision 3: the gate is for
           portal customers, because a helper on a late job has to be able to
           reach an admin at any hour. -->

      @if (panelError(); as message) {
        <p class="afs-chat-panel__error" role="alert">{{ message }}</p>
      }

      <app-chat-tabs
        [conversations]="chat.conversations()"
        [watching]="chat.watching()"
        [activeId]="chat.activeId()"
        [unread]="chat.unread()"
        (select)="chat.select($event)"
      />

      @if (active(); as conversation) {
        <app-chat-thread
          [messages]="chat.messages()"
          [participants]="chat.participants()"
          [selfUserId]="selfUserId()"
          [typingName]="chat.typingName()"
        />

        <div class="afs-chat-panel__actions">
          <button class="afs-chat-panel__back btn btn-sm btn-outline-secondary"
                  type="button" (click)="chat.select(null)">Back</button>

          @if (!watchingActive() && conversation.status !== 'closed') {
            <button class="afs-chat-panel__close-chat btn btn-sm btn-outline-secondary"
                    type="button" (click)="chat.closeChat()">Close</button>
          }
          @if (!watchingActive() && conversation.status === 'closed') {
            <button class="afs-chat-panel__reopen btn btn-sm btn-outline-secondary"
                    type="button" (click)="chat.reopen()">Reopen</button>
          }
        </div>

        <app-chat-composer
          [disabled]="watchingActive() || conversation.status === 'closed'"
          [busy]="chat.busy()"
          (send)="onSend($event)"
          (typing)="chat.notifyTyping()"
        />
      } @else {
        <app-chat-staff
          [staff]="chat.staff()"
          [busy]="chat.busy()"
          (start)="onStart($event)"
        />
      }
    </section>
  `,
})
export class ChatPanelComponent {
  readonly chat = inject(SurveyChatService);
  private readonly identity = inject(ChatIdentityService);

  readonly dismiss = output<void>();

  private readonly composer = viewChild(ChatComposerComponent);
  private readonly staffList = viewChild(ChatStaffComponent);

  /** The API's wording for a refused break, and nothing else. */
  readonly breakRefusal = signal<string | null>(null);

  /**
   * Everything that is not the break's own refusal. The break control already
   * prints that one, and two `role="alert"` elements holding the same sentence
   * say it twice.
   */
  readonly panelError = computed(() => {
    const message = this.chat.error();
    return message === this.breakRefusal() ? null : message;
  });

  /** The conversation showing in the thread, whether owned or only watched. */
  readonly active = computed(() => {
    const id = this.chat.activeId();
    if (id === null) return null;
    return [...this.chat.conversations(), ...this.chat.watching()]
      .find((c) => c.id === id) ?? null;
  });

  readonly watchingActive = computed(() => {
    const id = this.chat.activeId();
    return id !== null && this.chat.watching().some((c) => c.id === id);
  });

  /**
   * Only the identity service knows this. `conversation.createdBy` is whoever
   * opened the thread, which is the wrong answer for every chat this person did
   * not start. The empty-string fallback degrades to "nothing is mine" rather
   * than to a crash.
   */
  readonly selfUserId = computed(() => this.identity.userId() ?? '');

  async onSend(body: string): Promise<void> {
    await this.chat.send(body);

    // The composer clears optimistically, so a failure has to hand the text
    // back. `send` clears error() on the way in, so a non-null value here is
    // this send's own failure and not something left over.
    if (this.chat.error() !== null) this.composer()?.restore(body);
  }

  async onStart(request: { userId: string | null; firstMessage: string }): Promise<void> {
    await this.chat.startWith(request.userId, request.firstMessage);

    // Same failure and the same fix as a send. A start that succeeded has
    // already switched to the thread, so the staff list is gone and this is a
    // no-op — including when it was only the invite that failed.
    if (this.chat.error() !== null) this.staffList()?.restore(request.firstMessage);
  }

  async onStartBreak(request: { minutes: number; reason: string | null }): Promise<void> {
    await this.chat.startBreak(request.minutes, request.reason);
    this.breakRefusal.set(this.chat.error());
  }

  async onEndBreak(): Promise<void> {
    await this.chat.endBreak();
    this.breakRefusal.set(this.chat.error());
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') return;
    this.dismiss.emit();
  }
}
